import { RiDraggable } from 'component-library';
import { FeatureCollection, Point } from 'geojson';
import { MouseEvent, ReactNode, RefObject, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, MapRef, Marker, Source } from 'react-map-gl';

import { DraftPlot, PlotType } from '@/api/rest/resources/types/plot';
import { Map as LandlerMap, PlotTypeIcon } from '@/components';
import { getPNGIconForLandType } from '@/utils/getPNGIconForLandType';

import { usePlotLocation } from '../hooks/usePlotLocation';

type DraftPlotsPageLayoutProps = {
  'data-testid'?: string;
  panelWidth: number;
  /** When true, the left panel is hidden and only the drag handle remains to re-expand it. */
  isPanelCollapsed?: boolean;
  onDragHandleMouseDown: (e: MouseEvent<HTMLDivElement>) => void;
  mapRef: RefObject<MapRef>;
  allDraftPlots: DraftPlot[];
  draftPlotsGeoJSON: FeatureCollection | null;
  draftPlotCentroidsGeoJSON: FeatureCollection<Point> | null;
  hoveredPlot: DraftPlot | undefined;
  hoveredCentroid: { lng: number; lat: number } | undefined;
  onMapLoad: () => void;
  setHoveredPlotId: (id: string | null) => void;
  /** Extra map content rendered after the shared plot layers (e.g. a manual-entry preview pin). */
  mapChildren?: ReactNode;
  /** The full left-panel content: header, scrollable body, and footer actions. */
  children: ReactNode;
};

/**
 * Shared three-column shell used by both UploadPlotsPage and UploadCoordinatesPage:
 * [ resizable left panel ] [ drag handle ] [ map ]
 */
export const DraftPlotsPageLayout = ({
  'data-testid': dataTestId,
  panelWidth,
  isPanelCollapsed,
  onDragHandleMouseDown,
  mapRef,
  allDraftPlots,
  draftPlotsGeoJSON,
  draftPlotCentroidsGeoJSON,
  hoveredPlot,
  hoveredCentroid,
  onMapLoad,
  setHoveredPlotId,
  mapChildren,
  children,
}: DraftPlotsPageLayoutProps) => {
  const { t } = useTranslation();
  const [selectedMapPlotId, setSelectedMapPlotId] = useState<string | null>(null);

  const selectedMapPlot = useMemo(
    () => (selectedMapPlotId ? allDraftPlots.find((plot) => plot.id === selectedMapPlotId) : undefined),
    [allDraftPlots, selectedMapPlotId],
  );
  const activeSummaryPlot = selectedMapPlot ?? hoveredPlot;
  const activeCentroid = selectedMapPlot
    ? (() => {
        const feature = draftPlotCentroidsGeoJSON?.features.find((f) => f.properties?.id === selectedMapPlot.id);
        const [lng, lat] = feature?.geometry.coordinates ?? [];
        if (typeof lng !== 'number' || typeof lat !== 'number') return undefined;
        return { lng, lat };
      })()
    : hoveredCentroid;

  const { location, isLoading: isLocationLoading } = usePlotLocation(activeSummaryPlot?.multi_polygon ?? null);

  const handleMapLoad = () => {
    const map = mapRef.current;
    if (map) {
      Object.values(PlotType).forEach((plotType) => {
        if (!map.hasImage?.(`${plotType}-icon`)) {
          map.loadImage?.(getPNGIconForLandType(plotType), (error, image) => {
            if (error || !image) return;
            map.addImage(`${plotType}-icon`, image, { sdf: true });
          });
        }
      });
    }
    onMapLoad();
  };

  return (
    <div className='flex h-screen overflow-hidden bg-white-100' data-testid={dataTestId}>
      {/* Left panel */}
      {!isPanelCollapsed && (
        <div
          style={{ width: panelWidth }}
          className='flex h-full flex-col overflow-hidden border-r border-neutral-black-12'
        >
          {children}
        </div>
      )}

      {/* Drag handle — pill affordance with a drag icon; click toggles 539↔982px (or re-expands when collapsed), drag snaps on release */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className='flex w-[22px] shrink-0 cursor-col-resize items-center justify-center border-r border-neutral-black-12'
        onMouseDown={onDragHandleMouseDown}
      >
        <div className='flex h-[62px] w-5 items-center justify-center rounded-full bg-primary-100'>
          <RiDraggable size={12} className='rotate-90 text-white-100' />
        </div>
      </div>

      {/* Map */}
      <div className='relative min-w-0 flex-1 overflow-hidden'>
        <div className='absolute inset-0'>
          <LandlerMap
            ref={mapRef}
            style={{ width: '100%', height: '100%' }}
            onLoad={handleMapLoad}
            onClick={(e) => {
              const map = mapRef.current;
              if (!map) return;
              const layers = ['draft-plots-fill', 'draft-plot-icon-bg', 'draft-plot-icons'].filter((id) =>
                map.getLayer(id),
              );
              if (layers.length === 0) return;
              const [topFeature] = map.queryRenderedFeatures(e.point, { layers });
              const clickedId = (topFeature?.properties?.id as string | undefined) ?? null;
              if (!clickedId) {
                setSelectedMapPlotId(null);
                return;
              }
              setSelectedMapPlotId((prev) => (prev === clickedId ? null : clickedId));
            }}
            onMouseMove={(e) => {
              const map = mapRef.current;
              if (!map) return;
              const layers = ['draft-plots-fill', 'draft-plot-icon-bg', 'draft-plot-icons'].filter((id) =>
                map.getLayer(id),
              );
              if (layers.length === 0) return;
              const [topFeature] = map.queryRenderedFeatures(e.point, { layers });
              setHoveredPlotId((topFeature?.properties?.id as string) ?? null);
            }}
            onMouseLeave={() => setHoveredPlotId(null)}
          >
            {draftPlotsGeoJSON && (
              <Source id='draft-plots' type='geojson' data={draftPlotsGeoJSON}>
                <Layer id='draft-plots-fill' type='fill' paint={{ 'fill-color': '#22c55e', 'fill-opacity': 0.3 }} />
                <Layer id='draft-plots-outline' type='line' paint={{ 'line-color': '#16a34a', 'line-width': 2 }} />
              </Source>
            )}
            {draftPlotCentroidsGeoJSON && (
              <Source id='draft-plot-centroids' type='geojson' data={draftPlotCentroidsGeoJSON}>
                <Layer
                  id='draft-plot-icon-bg'
                  type='circle'
                  paint={{
                    'circle-radius': 14,
                    'circle-color': '#ffffff',
                    'circle-stroke-width': 1,
                    'circle-stroke-color': 'rgba(0, 0, 0, 0.12)',
                  }}
                />
                <Layer
                  id='draft-plot-icons'
                  type='symbol'
                  layout={{
                    'icon-image': ['get', 'icon'],
                    'icon-size': 0.5,
                    'icon-allow-overlap': true,
                  }}
                  paint={{
                    'icon-color': ['case', ['get', 'selected'], '#16a34a', '#374151'],
                    'icon-opacity': 1,
                  }}
                />
              </Source>
            )}
            {activeSummaryPlot && activeCentroid && (
              <Marker longitude={activeCentroid.lng} latitude={activeCentroid.lat} anchor='bottom'>
                <div className='w-[300px] rounded-2xl bg-white-100 p-4 shadow-md ring-1 ring-neutral-black-12'>
                  <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
                    <div className='col-span-2'>
                      <span className='typography-overline whitespace-nowrap text-text-secondary'>
                        {t('shared.uploadPlots.editPlot.fields.name')}
                      </span>
                      <p className='typography-body1'>{activeSummaryPlot.name}</p>
                    </div>
                    <div>
                      <span className='typography-overline whitespace-nowrap text-text-secondary'>
                        {t('shared.uploadPlots.editPlot.fields.plotArea')}
                      </span>
                      <p className='typography-body1'>{(activeSummaryPlot.area / 10_000).toFixed(2)} ha</p>
                    </div>
                    <div>
                      <span className='typography-overline whitespace-nowrap text-text-secondary'>
                        {t('shared.uploadPlots.newGroup.fields.plotLandType')}
                      </span>
                      <p className='typography-body1 flex items-center gap-1.5'>
                        <PlotTypeIcon type={activeSummaryPlot.land_type} size={16} />
                        <span>{t(`global.plotTypes.${activeSummaryPlot.land_type}`)}</span>
                      </p>
                    </div>
                    <div className='col-span-2'>
                      <span className='typography-overline whitespace-nowrap text-text-secondary'>
                        {t('shared.uploadPlots.editPlot.fields.location')}
                      </span>
                      <p className='typography-body1'>
                        {isLocationLoading
                          ? '…'
                          : (location ?? t('shared.uploadPlots.editPlot.placeholders.noLocation'))}
                      </p>
                    </div>
                  </div>
                </div>
              </Marker>
            )}
            {mapChildren}
          </LandlerMap>
        </div>
      </div>
    </div>
  );
};
